import clsx from 'clsx';
import { GripVertical, MapPin, Clock } from 'lucide-react';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate } from '@shared/utils';
import { SOURCE_BADGE } from '../constants';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function ServiceMonthBadges({ cycleMonths, oilChangeMonth }) {
  const months = Array.isArray(cycleMonths) ? [...cycleMonths].sort((a, b) => a - b) : [];
  if (months.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {months.map((month) => {
        const isService = oilChangeMonth === month;
        return (
          <span
            key={month}
            className={clsx(
              'inline-flex items-center px-1 py-0.5 rounded text-2xs font-medium border',
              isService
                ? 'bg-amber-100 text-amber-800 border-amber-300'
                : 'bg-gray-100 text-gray-700 border-transparent'
            )}
            title={isService ? 'Service (oil change)' : 'Inspection'}
          >
            {MONTH_SHORT[month - 1]}
            <span className="ml-0.5 opacity-80">{isService ? 'S' : 'I'}</span>
          </span>
        );
      })}
    </div>
  );
}

function ClientFlagIcons({ flags }) {
  if (!flags || flags.length === 0) return null;
  const visible = flags.slice(0, 4);
  const remaining = flags.length - visible.length;
  return (
    <div className="flex items-center gap-1">
      {visible.map((flag) => (
        <span key={flag.id} title={flag.name}>
          <FlagIcon
            iconName={flag.icon}
            className={clsx('h-3 w-3', flag.isActive ? 'text-gray-600' : 'text-gray-400')}
            title={flag.name}
          />
        </span>
      ))}
      {remaining > 0 && (
        <span className="text-2xs text-gray-500" title={flags.slice(4).map((f) => f.name).join(', ')}>
          +{remaining}
        </span>
      )}
    </div>
  );
}

export default function BacklogCard({ item, onDragStart, onDragEnd, onClick, onOpenServiceCall, isDragging }) {
  const badge = SOURCE_BADGE[item.source] || SOURCE_BADGE.contract;
  const hasFlags = Array.isArray(item.flags) && item.flags.length > 0;

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.key || item.serviceCallId || item.refId);
        onDragStart?.(item);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onClick?.(item)}
      className={clsx(
        'hub-card-draggable',
        isDragging && 'opacity-50 border-primary-400',
        item.dueBucket === 'overdue' && 'border-l-2 border-l-red-500'
      )}
    >
      <GripVertical className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-gray-900 truncate text-xs">{item.displayName}</span>
          <span className={clsx('flex-shrink-0 px-1 py-0.5 rounded text-2xs font-medium', badge.className)}>
            {badge.label}
          </span>
          {item.scheduleGroup && (
            <span
              className="flex-shrink-0 px-1 py-0.5 rounded text-2xs font-medium bg-purple-100 text-purple-800"
              title={`Linked: ${item.scheduleGroup.name}`}
            >
              {item.scheduleGroup.name}
            </span>
          )}
        </div>

        {item.cityState && (
          <div className="flex items-center gap-1 text-2xs text-gray-500 mt-0.5">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{item.cityState}</span>
          </div>
        )}

        <ServiceMonthBadges cycleMonths={item.serviceCycleMonths} oilChangeMonth={item.oilChangeMonth} />

        {(hasFlags || item.lastServiceCallId || item.lastServiceDate) && (
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {hasFlags && <ClientFlagIcons flags={item.flags} />}
            {item.lastServiceDate && (
              item.lastServiceCallId ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenServiceCall?.(item.lastServiceCallId);
                  }}
                  className="inline-flex items-center gap-1 text-2xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  title="Open last service call"
                >
                  <Clock className="h-3 w-3" />
                  Last: {formatDate(item.lastServiceDate)}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 text-2xs text-gray-500">
                  <Clock className="h-3 w-3" />
                  Last: {formatDate(item.lastServiceDate)}
                </span>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
